import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["step", "indicator"]
  static values = { currentStep: { type: Number, default: 0 } }

  connect() {
    this.showCurrentStep()
  }

  next(event) {
    event.preventDefault()

    // Validate current step before proceeding
    const currentStepElement = this.stepTargets[this.currentStepValue]
    const inputs = currentStepElement.querySelectorAll('input[required], select[required], textarea[required]')

    let isValid = true
    inputs.forEach(input => {
      if (!input.checkValidity()) {
        input.reportValidity()
        isValid = false
      }
    })

    if (!isValid) return

    if (this.currentStepValue < this.stepTargets.length - 1) {
      this.currentStepValue++
      this.showCurrentStep()
    }
  }

  previous(event) {
    event.preventDefault()
    if (this.currentStepValue > 0) {
      this.currentStepValue--
      this.showCurrentStep()
    }
  }

  goToStep(event) {
    event.preventDefault()
    const targetStep = parseInt(event.currentTarget.dataset.step)

    // If going forward, validate current step first
    if (targetStep > this.currentStepValue) {
      const currentStepElement = this.stepTargets[this.currentStepValue]
      const inputs = currentStepElement.querySelectorAll('input[required], select[required], textarea[required]')

      let isValid = true
      inputs.forEach(input => {
        if (!input.checkValidity()) {
          input.reportValidity()
          isValid = false
        }
      })

      if (!isValid) return
    }

    this.currentStepValue = targetStep
    this.showCurrentStep()
  }

  showCurrentStep() {
    // Update step visibility
    this.stepTargets.forEach((step, index) => {
      if (index === this.currentStepValue) {
        step.classList.remove('hidden')
        // Dispatch event when step becomes visible
        step.dispatchEvent(new CustomEvent('step:visible', { bubbles: true, detail: { stepIndex: index } }))
      } else {
        step.classList.add('hidden')
      }
    })

    // Update step indicators
    this.indicatorTargets.forEach((indicator, index) => {
      const isCompleted = index < this.currentStepValue
      const isCurrent = index === this.currentStepValue

      // Update the indicator's state
      if (isCompleted) {
        indicator.dataset.state = 'completed'
      } else if (isCurrent) {
        indicator.dataset.state = 'current'
      } else {
        indicator.dataset.state = 'upcoming'
      }

      this.updateIndicatorStyles(indicator, isCompleted, isCurrent)
    })
  }

  updateIndicatorStyles(indicator, isCompleted, isCurrent) {
    const numberElement = indicator.querySelector('[data-step-number]')
    const checkElement = indicator.querySelector('[data-step-check]')
    const textElement = indicator.querySelector('[data-step-text]')

    if (isCompleted) {
      // Completed step: show checkmark instead of number, primary color
      if (numberElement) {
        numberElement.classList.add('hidden')
      }
      if (checkElement) {
        checkElement.classList.remove('hidden')
      }
      // Apply primary color to text
      indicator.classList.remove('text-gray-500', 'dark:text-gray-400')
      indicator.classList.add('text-primary-600', 'dark:text-primary-500')
    } else if (isCurrent) {
      // Current step: show number with primary border, primary color text
      if (numberElement) {
        numberElement.classList.remove('hidden', 'border-gray-300', 'dark:border-gray-600')
        numberElement.classList.add('border-primary-600', 'dark:border-primary-500')
      }
      if (checkElement) {
        checkElement.classList.add('hidden')
      }
      // Apply primary color to text
      indicator.classList.remove('text-gray-500', 'dark:text-gray-400')
      indicator.classList.add('text-primary-600', 'dark:text-primary-500')
    } else {
      // Upcoming step: show number with gray border, gray color text
      if (numberElement) {
        numberElement.classList.remove('hidden', 'border-primary-600', 'dark:border-primary-500')
        numberElement.classList.add('border-gray-300', 'dark:border-gray-600')
      }
      if (checkElement) {
        checkElement.classList.add('hidden')
      }
      // Apply gray color to text
      indicator.classList.remove('text-primary-600', 'dark:text-primary-500')
      indicator.classList.add('text-gray-500', 'dark:text-gray-400')
    }
  }
}
